import React from 'react';
import { useScreenReader } from '../../context/AccessibilityContext';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'gradient' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  glow?: boolean;
  pulse?: boolean;
  screenReaderText?: string;
  keyboardShortcut?: string;
}

export function Button({
  children,
  className = '',
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  glow = false,
  pulse = false,
  screenReaderText,
  keyboardShortcut,
  onClick,
  ...props
}: ButtonProps) {
  const { announceAction } = useScreenReader();
  const baseClasses = "relative inline-flex items-center justify-center font-medium transition-all duration-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-naia-pink focus:ring-offset-2 focus:ring-offset-deep-night disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden group";

  const variantClasses = {
    primary: "bg-gradient-to-r from-naia-pink to-pink-light text-pearl-white hover:from-pink-light hover:to-naia-pink hover:scale-105 active:scale-95 elevation-2 hover:elevation-3",
    secondary: "bg-surface-raised border border-surface-border text-text-primary hover:bg-surface-high hover:border-naia-pink/50 hover:text-naia-pink elevation-1 hover:elevation-2",
    ghost: "text-text-secondary hover:text-text-primary hover:bg-white/5 active:bg-white/10",
    outline: "border-2 border-naia-pink text-naia-pink hover:bg-naia-pink hover:text-pearl-white active:bg-pink-dark",
    gradient: "bg-gradient-to-r from-naia-pink via-soft-violet to-cyan-accent text-pearl-white hover:scale-105 active:scale-95 elevation-3 hover:elevation-4 animate-gradient-x bg-size-200",
    danger: "bg-gradient-to-r from-error to-red-600 text-pearl-white hover:from-red-600 hover:to-error hover:scale-105 active:scale-95 elevation-2 hover:elevation-3"
  };

  const sizeClasses = {
    xs: "px-2 py-1 text-xs min-h-[28px] gap-1",
    sm: "px-3 py-1.5 text-sm min-h-[36px] gap-2",
    md: "px-4 py-2 text-base min-h-[44px] gap-2",
    lg: "px-6 py-3 text-lg min-h-[52px] gap-3",
    xl: "px-8 py-4 text-xl min-h-[60px] gap-3"
  };

  const glowClass = glow ? (variant === 'primary' ? 'naia-glow' : 'cyan-glow') : '';
  const pulseClass = pulse ? 'animate-pulse' : '';
  const fullWidthClass = fullWidth ? 'w-full' : '';

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (screenReaderText) {
      announceAction(screenReaderText);
    }
    onClick?.(e);
  };

  return (
    <button
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${glowClass}
        ${pulseClass}
        ${fullWidthClass}
        ${className}
      `.trim()}
      disabled={disabled || loading}
      onClick={handleClick}
      aria-label={screenReaderText || props['aria-label']}
      data-keyboard-shortcut={keyboardShortcut}
      {...props}
    >
      {/* Background animation effect */}
      <div className="absolute inset-0 -z-10 opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-gradient-to-r from-white to-transparent" />
      
      {/* Loading spinner */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      
      {/* Content */}
      <div className={`flex items-center gap-inherit ${loading ? 'opacity-0' : 'opacity-100'}`}>
        {icon && iconPosition === 'left' && (
          <span className="flex-shrink-0 transition-transform group-hover:scale-110">
            {icon}
          </span>
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <span className="flex-shrink-0 transition-transform group-hover:scale-110">
            {icon}
          </span>
        )}
      </div>
    </button>
  );
}

// Floating Action Button
interface FABProps extends Omit<ButtonProps, 'variant' | 'size'> {
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: 'sm' | 'md' | 'lg';
}

export function FloatingActionButton({
  children,
  className = '',
  position = 'bottom-right',
  size = 'md',
  ...props
}: FABProps) {
  const positionClasses = {
    'bottom-right': 'fixed bottom-6 right-6',
    'bottom-left': 'fixed bottom-6 left-6',
    'top-right': 'fixed top-6 right-6',
    'top-left': 'fixed top-6 left-6'
  };

  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-14 h-14',
    lg: 'w-16 h-16'
  };

  return (
    <Button
      variant="primary"
      glow
      className={`
        ${positionClasses[position]}
        ${sizeClasses[size]}
        !rounded-full !p-0 z-50 shadow-2xl hover:shadow-naia-glow
        ${className}
      `.trim()}
      {...props}
    >
      {children}
    </Button>
  );
}

// Button Group
interface ButtonGroupProps {
  children: React.ReactNode;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
  fullWidth?: boolean;
}

export function ButtonGroup({
  children,
  className = '',
  orientation = 'horizontal',
  fullWidth = false
}: ButtonGroupProps) {
  const orientationClass = orientation === 'horizontal' ? 'flex-row' : 'flex-col';
  const fullWidthClass = fullWidth ? 'w-full' : '';

  return (
    <div className={`
      flex ${orientationClass} ${fullWidthClass}
      [&>button:not(:first-child)]:${orientation === 'horizontal' ? '-ml-px' : '-mt-px'}
      [&>button:not(:first-child):not(:last-child)]:rounded-none
      [&>button:first-child]:${orientation === 'horizontal' ? 'rounded-r-none' : 'rounded-b-none'}
      [&>button:last-child]:${orientation === 'horizontal' ? 'rounded-l-none' : 'rounded-t-none'}
      ${className}
    `.trim()}>
      {children}
    </div>
  );
} 